import { MarkedDates } from 'react-native-calendars/src/types';
import theme from 'shared/styles/theme';

const isFirstDateGreater = (date1: Date, date2: Date): boolean => {
  //ignores the timezone offset
  const date1UTC = new Date(
    Date.UTC(date1.getUTCFullYear(), date1.getUTCMonth(), date1.getUTCDate()),
  );
  const date2UTC = new Date(
    Date.UTC(date2.getUTCFullYear(), date2.getUTCMonth(), date2.getUTCDate()),
  );
  // return true if the date1 is > date2
  return date1UTC.getTime() > date2UTC.getTime();
};

const diffDays = (date1: Date, date2: Date): number => {
  // get the absolute difference between the two dates
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  // return the difference in days
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

const toDateString = (date: Date): string => {
  // convert to UTC (to correct for timezone offset)
  const utcDate = new Date(date.toISOString());
  // get the date parts
  const year = utcDate.getUTCFullYear();
  const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getUTCDate()).padStart(2, '0');
  // return the formatted date
  return `${year}-${month}-${day}`;
};

const isHoliday = (date: string, holidayList: MarkedDates): boolean => {
  const checkingDate = toDateString(new Date(date));
  return !!holidayList[checkingDate];
};

const generateSelectedDateObject = (
  startDate: string | null,
  endDate: string | null,
  holidayListObject: MarkedDates,
) => {
  const dateRangeObject: MarkedDates = {};
  if (!startDate) {
    return dateRangeObject;
  }
  const rangeEndDate = endDate ?? startDate;
  let startDateObj = new Date(startDate);
  let endDateObj = new Date(rangeEndDate);

  const range = endDate ? diffDays(startDateObj, endDateObj) : 0;

  if (range > 0) {
    if (isFirstDateGreater(new Date(startDate), new Date(rangeEndDate))) {
      startDateObj = new Date(rangeEndDate);
      endDateObj = new Date(startDate);
    }
    const currentDate = new Date(startDateObj);
    dateRangeObject[toDateString(currentDate)] = dateRangeObject[
      toDateString(currentDate)
    ]
      ? dateRangeObject[toDateString(currentDate)]
      : {
          startingDay: true,
          selected: true,
          textColor: theme.color.text.inverse,
          customStyles: {
            container: {
              backgroundColor: theme.color.interactive.primary,
              borderBottomLeftRadius: 5,
              borderTopLeftRadius: 5,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              width: '100%',
            },
          },
        };

    for (let i = 0; i < range; i++) {
      currentDate.setDate(currentDate.getDate() + 1);
      if (!isHoliday(toDateString(currentDate), holidayListObject)) {
        dateRangeObject[toDateString(currentDate)] = {
          selected: true,
          textColor: theme.color.text.primary,
          customStyles: {
            container: {
              backgroundColor: theme.color.expressive.orchid,
              width: '100%',
              borderRadius: 0,
            },
          },
        };
      }
    }

    if (!isHoliday(toDateString(endDateObj), holidayListObject)) {
      dateRangeObject[toDateString(endDateObj)] = {
        selected: true,
        endingDay: true,
        customStyles: {
          container: {
            backgroundColor: theme.color.interactive.primary,
            borderBottomRightRadius: 5,
            borderTopRightRadius: 5,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            width: '100%',
          },
        },
      };
    }
  } else if (!isHoliday(toDateString(startDateObj), holidayListObject)) {
    dateRangeObject[toDateString(startDateObj)] = {
      selected: true,
      color: theme.color.interactive.primary,
      textColor: theme.color.text.inverse,
      selectedColor: theme.color.interactive.primary,
      selectedTextColor: theme.color.text.inverse,
    };
  }
  const copyOfDateRangeObject = { ...dateRangeObject };
  return copyOfDateRangeObject;
};

export {
  diffDays,
  generateSelectedDateObject,
  isFirstDateGreater,
  isHoliday,
  toDateString,
};
